'use client';

import { useTranslations } from 'next-intl';
import { useImperativeHandle, MouseEvent } from 'react';

import { UiKitButtonBase } from '@/fo/ui/kit/button/base';

import { PaginationArrow } from '../components/PaginationArrow/PaginationArrow';
import { PaginationButton } from '../components/PaginationButton/PaginationButton';
import { usePagination } from '../hooks/usePagination';

import { styles } from './UiKitPaginationBase.styles';

export interface UiKitPaginationBaseRef {
  reset: () => void;
}

export interface UiKitPaginationBaseProps {
  /**
   * Общее количество страниц
   */
  count: number;
  /**
   * Текущая страница
   * @default 1
   */
  page?: number;

  onChange?: (page: number) => void;
  disabled?: boolean;

  /**
   * Ссылка на объект пагинации в котором доступен метод reset
   */
  ref?: React.Ref<UiKitPaginationBaseRef>;

  /**
   * Функция для генерации ссылок на страницы пагинации
   * Используется для Next.js навигации
   */
  getPageHref?: (page: number) => string;

  /**
   * Отключает навигацию по ссылкам
   * @default false
   */
  disabledNavigation?: boolean;

  /**
   * Отключает кнопку "Показать ещё"
   * @default false
   */
  disabledMore?: boolean;

  /**
   * Обработчик для кнопки "Показать ещё"
   */
  onLoadMore?: () => void;
}

export function UiKitPaginationBase({
  count,
  page,
  onChange,
  disabled,
  getPageHref = (page) => `?page=${page}`,
  ref,
  disabledNavigation = false,
  disabledMore = false,
  onLoadMore
}: UiKitPaginationBaseProps) {
  const t = useTranslations();

  const { items, reset, currentPage, nextPage } = usePagination({
    count,
    page,
    onChange,
    disabled
  });

  const slots = styles();

  const onMoreButtonClick = (
    event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    if (disabledNavigation || onLoadMore) {
      event.preventDefault();
    }

    if (onLoadMore) {
      onLoadMore();
    } else {
      nextPage();
    }
  };

  useImperativeHandle(ref, () => ({
    reset
  }));

  return (
    <nav className={slots.base()}>
      <div className={slots.pagination()}>
        {items.map((item, index) => {
          if (item.type === 'previous') {
            return (
              <PaginationArrow
                key={index}
                direction="left"
                onClick={() => item.onClick(item.page)}
                disabled={item.disabled}
                href={getPageHref(item.page ?? 1)}
                disabledNavigation={disabledNavigation}
              />
            );
          }

          if (item.type === 'next') {
            return (
              <PaginationArrow
                key={index}
                direction="right"
                onClick={() => item.onClick(item.page)}
                disabled={item.disabled}
                href={getPageHref(item.page ?? count)}
                disabledNavigation={disabledNavigation}
              />
            );
          }

          if (item.type === 'ellipsis') {
            return (
              <span
                key={index}
                className={slots.ellipsis()}
              >
                ...
              </span>
            );
          }

          return (
            <PaginationButton
              key={index}
              onClick={() => item.onClick(item.page)}
              disabled={item.disabled}
              selected={item.selected}
              href={getPageHref(item.page ?? 1)}
              disabledNavigation={disabledNavigation}
            >
              {item.page}
            </PaginationButton>
          );
        })}
      </div>
      {currentPage !== count && !disabledMore && (
        <div className={slots.more()}>
          <UiKitButtonBase
            as="button"
            onClick={onMoreButtonClick}
            color="primary"
            className="w-full"
          >
            {t('showMore')}
          </UiKitButtonBase>
        </div>
      )}
    </nav>
  );
}
